import { useState, useEffect } from 'react';
import { X, Smartphone, Download } from 'lucide-react';

/**
 * Mobile App Download Banner
 * - Shows on Android mobile devices
 * - Promotes app download from Play Store
 * - Remembers dismiss for 7 days
 * - Sticky bottom banner with slide-up animation
 */
export default function MobileAppBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if we should show the banner
    const shouldShow = checkShouldShowBanner();
    if (shouldShow) {
      // Delay animation for smooth entrance
      setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, 1000);
    }
  }, []);

  const checkShouldShowBanner = (): boolean => {
    // 1. Check if user is on mobile Android
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isMobile = /Mobile/i.test(navigator.userAgent);
    
    if (!isAndroid || !isMobile) {
      return false;
    }

    // 2. Check if banner was dismissed
    const dismissedAt = localStorage.getItem('mobileAppBannerDismissed');
    if (dismissedAt) {
      const dismissedTime = new Date(dismissedAt).getTime();
      const now = new Date().getTime();
      const daysSinceDismiss = (now - dismissedTime) / (1000 * 60 * 60 * 24);
      
      // Show again after 7 days
      if (daysSinceDismiss < 7) {
        return false;
      }
    }

    // 3. Check if user came from app (has app token or special param)
    const urlParams = new URLSearchParams(window.location.search);
    const fromApp = urlParams.get('source') === 'app' || 
                    document.referrer.includes('com.metapic.app');
    
    if (fromApp) {
      return false; // Don't show if user is already using app
    }

    return true;
  };

  const handleDismiss = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      // Remember dismiss for 7 days
      localStorage.setItem('mobileAppBannerDismissed', new Date().toISOString());
    }, 300);
  };

  const handleDownload = () => {
    // Open Play Store link
    window.open(
      'https://play.google.com/store/apps/details?id=com.metapic.app',
      '_blank'
    );
    
    // Track download click (optional - add analytics here)
    console.log('📱 User clicked mobile app download');
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-out ${
        isAnimating ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      {/* Backdrop shadow */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" 
           style={{ height: '120px', bottom: '100%' }} 
      />
      
      {/* Banner Card */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white mb-0.5">
                📱 Get the MetaPic App
              </p>
              <p className="text-xs text-white/90">
                Better experience on Android
              </p>
            </div>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 rounded-lg font-semibold text-sm hover:bg-indigo-50 transition-colors shadow-lg"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download</span>
              <span className="sm:hidden">Get</span>
            </button>

            {/* Close Button */}
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
